import { HTTP_INTERCEPTORS, provideHttpClient, withFetch, withInterceptorsFromDi } from '@angular/common/http';
import { APP_ID, APP_INITIALIZER, ErrorHandler } from '@angular/core';
import { provideClientHydration } from '@angular/platform-browser';
import { provideAnimations } from '@angular/platform-browser/animations';
import {
  Router,
  provideRouter,
  withEnabledBlockingInitialNavigation,
  withInMemoryScrolling,
  withRouterConfig,
} from '@angular/router';
import { TraceService } from '@sentry/angular-ivy';
import { CookieService } from 'ngx-cookie-service';
import { environment } from '../environments/environment';
import { AuthInterceptor } from './auth/interceptors/auth-interceptor';
import { ErrorInterceptor } from './auth/interceptors/error-interceptor';
import { ErrorRetryInterceptor } from './auth/interceptors/error-retry-interceptor';
import { ChromeExtensionRoutes } from './chrome-extension-routes';
import { CustomErrorHandlerService } from './error-handler';
import { SentryTransactionInterceptor } from './interceptors/sentry-transaction-interceptor';
import { AppConfigService } from './providers/config.service';
import { routes } from './routes';
import { AnalyticsService } from './services/analytics.service';
import { BrowserService } from './services/browser.service';
import { MetaService } from './services/meta.service';
import { ScrollService } from './services/scroll.service';
import { tenantRoutes } from './tenant-routes';

function initializeApp(
  scroll: ScrollService,
  an: AnalyticsService,
  meta: MetaService,
  config: AppConfigService,
  browserService: BrowserService,
  router: Router
) {
  return () => {
    const routes = getRoutes(browserService);
    if (routes) router.resetConfig(routes);
    console.debug('ANGULAR_INIT_APP');
  };
}

function getRoutes(browserService: BrowserService) {
  const isTenantDomain = browserService.isTenantDomain();
  if (browserService.isExtension()) {
    return ChromeExtensionRoutes;
  } else {
    if (isTenantDomain) return tenantRoutes;
  }
}

import { GROWTHBOOK, gb } from './growthbook';
import { PICFLOW_ANALYTICS } from './picflow-analytics';
import { PICFLOW_COMMENTS } from './picflow-comments';
import { PICFLOW_CONFIG } from './picflow-config';
import { CommentService } from './services/comment.service';

export const appConfig = {
  providers: [
    provideHttpClient(withFetch(), withInterceptorsFromDi()),
    ...(environment.extension ? [] : [provideClientHydration()]),
    provideAnimations(),
    CookieService,
    { provide: APP_ID, useValue: 'picflow' },
    { provide: HTTP_INTERCEPTORS, useClass: AuthInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: SentryTransactionInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorInterceptor, multi: true },
    { provide: HTTP_INTERCEPTORS, useClass: ErrorRetryInterceptor, multi: true },
    { provide: GROWTHBOOK, useFactory: gb, deps: [] },
    { provide: PICFLOW_CONFIG, useFactory: (config: AppConfigService) => config, deps: [AppConfigService] },
    { provide: PICFLOW_ANALYTICS, useFactory: (analytics: AnalyticsService) => analytics, deps: [AnalyticsService] },
    { provide: PICFLOW_COMMENTS, useFactory: (analytics: CommentService) => analytics, deps: [CommentService] },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ScrollService, AnalyticsService, MetaService, AppConfigService, BrowserService, Router],
      multi: true,
    },
    provideRouter(
      routes,
      withEnabledBlockingInitialNavigation(),
      withRouterConfig({
        onSameUrlNavigation: 'reload',
      }),
      withInMemoryScrolling({ scrollPositionRestoration: 'enabled', anchorScrolling: 'enabled' })
    ),
    { provide: ErrorHandler, useClass: CustomErrorHandlerService },
    {
      provide: TraceService,
      deps: [Router],
    },
  ],
};
